// This project has no tsconfig.json, so this reference is needed to pull in
// react-app-env.d.ts's ambient declarations (react-scripts types, *.css
// wildcard module) — an unimported .d.ts otherwise isn't part of this file's
// inferred TS project.
/// <reference path="../../../../../react-app-env.d.ts" />
import React, { useState, useEffect, useCallback } from 'react';
import { useRestaurant } from '../../contexts/RestaurantContext.tsx';
import { Rnd } from 'react-rnd';
import '../../styles/table-component.css';
import { CHAIR_SIZE_PX } from '../../constants.ts';

interface TableComponentProps {
  table: any;
  isSelected: boolean;
  isEditing: boolean;
  onClick: () => void;
  scale: number;
}

const TableComponent: React.FC<TableComponentProps> = ({
  table,
  isSelected,
  isEditing,
  onClick,
  scale
}) => {
  const { updateTable } = useRestaurant();

  const getTopLeft = useCallback((center: { x: number; y: number }, dims: { width: number; height: number }) => ({
    x: center.x - dims.width / 2,
    y: center.y - dims.height / 2
  }), []);

  const getCenter = useCallback((topLeft: { x: number; y: number }, dims: { width: number; height: number }) => ({
    x: topLeft.x + dims.width / 2,
    y: topLeft.y + dims.height / 2
  }), []);

  const initialSize = { width: table.shape.width, height: table.shape.height };
  const [size, setSize] = useState(initialSize);
  const [position, setPosition] = useState(getTopLeft(table.position, initialSize));
  const [isResizing, setIsResizing] = useState(false);

  useEffect(() => {
    const newSize = { width: table.shape.width, height: table.shape.height };
    const newPosition = getTopLeft(table.position, newSize);
    setSize(prev => {
      if (prev.width === newSize.width && prev.height === newSize.height) {
        return prev;
      }
      return newSize;
    });
    setPosition(prev => {
      if (prev.x === newPosition.x && prev.y === newPosition.y) {
        return prev;
      }
      return newPosition;
    });
  }, [table.position, table.position.x, table.position.y, table.shape.width, table.shape.height, getTopLeft]);

  const handleDragStop = (_e: any, d: any) => {
    const newPosition = { x: d.x, y: d.y };
    setPosition(newPosition);
    updateTable(table.id, { position: getCenter(newPosition, size) });
  };

  const handleResizeStart = () => {
    setIsResizing(true);
  };

  const handleResizeStop = (_e: any, _direction: any, ref: any, _delta: any, newPosition: any) => {
    setIsResizing(false);
    const newSize = {
      width: parseInt(ref.style.width),
      height: parseInt(ref.style.height)
    };
    const updatedTopLeft = { x: newPosition.x, y: newPosition.y };
    setSize(newSize);
    setPosition(updatedTopLeft);

    updateTable(table.id, {
      position: getCenter(updatedTopLeft, newSize),
      shape: { ...table.shape, width: newSize.width, height: newSize.height }
    });
  };
  
  // Determine the chair positions based on table shape and size
  const getChairPositions = () => {
    const { type } = table.shape;
    const chairs: { x: number; y: number; rotation: number }[] = [];
    const half = CHAIR_SIZE_PX / 2;

    if (type === 'square' || type === 'rectangle') {
      const dist = [0, 0, 0, 0];
      let remaining = table.maxCovers;
      for (let i = 0; i < 4; i++) {
        dist[i] = Math.floor(table.maxCovers / 4);
        remaining -= dist[i];
      }
      for (let i = 0; remaining > 0; i++) {
        dist[i % 4] += 1;
        remaining--;
      }

      if (dist[0] > 0) {
        const seg = size.width / dist[0];
        for (let i = 0; i < dist[0]; i++) {
          chairs.push({ x: seg * (i + 0.5) - half, y: -CHAIR_SIZE_PX, rotation: 0 });
        }
      }
      if (dist[1] > 0) {
        const seg = size.width / dist[1];
        for (let i = 0; i < dist[1]; i++) {
          chairs.push({ x: seg * (i + 0.5) - half, y: size.height, rotation: 180 });
        }
      }
      if (dist[2] > 0) {
        const seg = size.height / dist[2];
        for (let i = 0; i < dist[2]; i++) {
          chairs.push({ x: -CHAIR_SIZE_PX, y: seg * (i + 0.5) - half, rotation: 270 });
        }
      }
      if (dist[3] > 0) {
        const seg = size.height / dist[3];
        for (let i = 0; i < dist[3]; i++) {
          chairs.push({ x: size.width, y: seg * (i + 0.5) - half, rotation: 90 });
        }
      }
    } else if (type === 'circle') {
      const radius = size.width / 2;
      const dist = radius + half;
      const count = table.maxCovers;
      for (let i = 0; i < count; i++) {
        const angle = (i * 2 * Math.PI) / count;
        chairs.push({
          x: radius + dist * Math.cos(angle) - half,
          y: radius + dist * Math.sin(angle) - half,
          rotation: angle * 180 / Math.PI + 90
        });
      }
    }

    return chairs;
  };
  
  const chairPositions = getChairPositions();
  
  // Render table based on its shape
  const renderTable = () => {
    const { type } = table.shape;
    const style = table.useColor
      ? {
          backgroundColor: table.color || '#8B4513',
          borderRadius: type === 'circle' ? '50%' : '4px'
        }
      : {
          backgroundImage: `url(/textures/${table.texture}.jpg)`,
          borderRadius: type === 'circle' ? '50%' : '4px'
        };

    const textStyle = {
      color: table.textColor || '#000000'
    };

    return (
      <div className="table-top" style={style}>
        <span className="table-name" style={textStyle}>{table.name}</span>
      </div>
    );
  };
  
  return (
    <Rnd
      position={position}
      size={size}
      scale={scale}
      onDragStop={handleDragStop}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
      lockAspectRatio={table.shape.type === 'circle'}
      minWidth={60}
      minHeight={60}
      disableDragging={!isEditing}
      enableResizing={isEditing}
      className={`table-component ${isSelected ? 'selected' : ''}`}
      onClick={onClick}
    >
      {renderTable()}

      {/* Render chairs around the table */}
      {!isResizing && chairPositions.map((chair, index) => (
        <div
          key={index}
          className="chair"
          style={{
            left: chair.x,
            top: chair.y,
            transform: `rotate(${chair.rotation}deg)`,
            width: CHAIR_SIZE_PX,
            height: CHAIR_SIZE_PX,
            backgroundColor: table.chairColor || '#8B4513'
          }}
        ></div>
      ))}
    </Rnd>
  );
};

export default TableComponent;
